/**
 * Copyright 2022-present terrestris GmbH & Co. KG
 *
 * Redistribution and use in source and binary forms, with or without modification,
 * are permitted provided that the following conditions are met:
 *
 * 1. Redistributions of source code must retain the above copyright notice, this
 * list of conditions and the following disclaimer.
 *
 * 2. Redistributions in binary form must reproduce the above copyright notice,
 * this list of conditions and the following disclaimer in the documentation
 * and/or other materials provided with the distribution.
 *
 * THIS SOFTWARE IS PROVIDED BY THE COPYRIGHT HOLDERS AND CONTRIBUTORS "AS IS" AND
 * ANY EXPRESS OR IMPLIED WARRANTIES, INCLUDING, BUT NOT LIMITED TO, THE IMPLIED
 * WARRANTIES OF MERCHANTABILITY AND FITNESS FOR A PARTICULAR PURPOSE ARE DISCLAIMED.
 * IN NO EVENT SHALL THE COPYRIGHT HOLDER OR CONTRIBUTORS BE LIABLE FOR ANY DIRECT,
 * INDIRECT, INCIDENTAL, SPECIAL, EXEMPLARY, OR CONSEQUENTIAL DAMAGES (INCLUDING,
 * BUT NOT LIMITED TO, PROCUREMENT OF SUBSTITUTE GOODS OR SERVICES; LOSS OF USE,
 * DATA, OR PROFITS; OR BUSINESS INTERRUPTION) HOWEVER CAUSED AND ON ANY THEORY OF
 * LIABILITY, WHETHER IN CONTRACT, STRICT LIABILITY, OR TORT (INCLUDING NEGLIGENCE
 * OR OTHERWISE) ARISING IN ANY WAY OUT OF THE USE OF THIS SOFTWARE, EVEN IF ADVISED
 * OF THE POSSIBILITY OF SUCH DAMAGE.
 */
import React from 'react';

import {
  Alert,
  ConfigProvider
} from 'antd';

import deDE from 'antd/locale/de_DE';
import enGB from 'antd/locale/en_GB';

import {
  defaults as OlControlDefaults,
  FullScreen
} from 'ol/control';
import WMTSCapabilities from 'ol/format/WMTSCapabilities';
import OlLayerGroup from 'ol/layer/Group';
import ImageLayer from 'ol/layer/Image';
import Layer from 'ol/layer/Layer';
import OlLayerTile from 'ol/layer/Tile';
import OlMap from 'ol/Map';
import ImageWMS from 'ol/source/ImageWMS';
import Source from 'ol/source/Source';

import WMTS, {
  optionsFromCapabilities
} from 'ol/source/WMTS';
import OlView from 'ol/View';
import { createRoot } from 'react-dom/client';

import {
  Provider
} from 'react-redux';

import Logger from '@terrestris/base-util/dist/Logger';
import { ArrayTwoOrMore } from '@terrestris/base-util/dist/types';
import { MapContext } from '@terrestris/react-util';

import App from './App';
import i18n from './i18n';
import {
  store
} from './store/store';

import './indexMain.less';

const baseUrl = 'solar-lk-oldenburg.ipsyscon.de';
const legendBaseUrl = `https://${baseUrl}/geoserver/lk-oldenburg/ows?service=WMS&request=GetLegendGraphic&format=image%2Fpng&width=20&height=20&layer=`;
const projectLayerExtent = [724219, 6.64842e+06, 1.30845e+06, 7.19825e+06];
let backgroundLayers: ArrayTwoOrMore<Layer>;
const projectWmsUrl = `https://${baseUrl}/geoserver/lk-oldenburg/wms?`;
const ipsysconAttributions = '©IP SYSCON GmbH';

const getConfigLang = (lang: string) => {
  switch (lang) {
    case 'en':
      return enGB;
    case 'de':
      return deDE;
    default:
      return enGB;
  }
};

interface ProjectLayerConfig {
  layerName: string;
  name: string;
  hoverable: boolean;
  visible: boolean;
}

const getProjectLayerFromConfig = (layerConfig: ProjectLayerConfig): ImageLayer<ImageWMS> => {
  const legendUrl = legendBaseUrl + layerConfig.layerName;
  const layer = new ImageLayer({
    extent: projectLayerExtent,
    source: new ImageWMS({
      url: projectWmsUrl,
      crossOrigin: 'anonymous',
      params: {
        'LAYERS': layerConfig.layerName
      },
      ratio: 1,
      serverType: 'geoserver',
      attributions: ipsysconAttributions
    }),
    visible: layerConfig.visible
  });
  layer.set('name', layerConfig.name);
  layer.set('legendUrl', legendUrl);
  layer.set('hoverable', layerConfig.hoverable);
  return layer;
};

const getProjectLayers = (): ImageLayer<ImageWMS>[] => {
  const projectLayerConfigs: ProjectLayerConfig[] = [
    {
      layerName: 'lk_oldenburg_gruen', // todo: make configurable
      name: 'Gründach',
      hoverable: true,
      visible: false
    }, {
      layerName: 'lk_oldenburg_th',
      name: 'Solarthermie',
      hoverable: true,
      visible: false
    }, {
      layerName: 'lk_oldenburg_parkplatz_pv',
      name: 'Photovoltaik Parkplätze',
      hoverable: true,
      visible: false
    }, {
      layerName: 'lk_oldenburg_pv',
      name: 'Photovoltaik',
      hoverable: true,
      visible: true
    }
  ];

  return projectLayerConfigs.map(getProjectLayerFromConfig);
};

const getAdditionalInfoLayers = (): ImageLayer<ImageWMS>[] => {
  const projectLayerConfigs: ProjectLayerConfig[] = [
    {
      layerName: 'lk_oldenburg_parkplatz_grundflaeche',
      name: 'Parkplatzgrundflächen',
      hoverable: false,
      visible: true
    }, {
      layerName: 'lk_oldenburg_gebaeude',
      name: 'Gebäude',
      hoverable: false,
      visible: true
    }, {
      layerName: 'lk_ol_gemeinden',
      name: 'Gemeinden',
      hoverable: false,
      visible: true
    }
  ];

  return projectLayerConfigs.map(getProjectLayerFromConfig);
};

const getBackGroundLayers = async (): Promise<ArrayTwoOrMore<Layer<Source>>> => {
  const parser = new WMTSCapabilities();
  const response = await fetch('https://basemap.de/dienste/wmts_capabilities_web_raster.xml'); // todo: make configurable
  const text = await response.text();
  const result = parser.read(text);
  const date = new Date();
  const basemapAttributions = `© basemap.de / BKG ${date.toLocaleString('de', { month: 'long' })} ${date.getFullYear()}`;

  const optionsGray = optionsFromCapabilities(result, {
    layer: 'de_basemapde_web_raster_grau', // todo: make configurable
    matrixSet: 'EPSG:3857',
    projection: 'EPSG:3857'
  });
  const optionsColor = optionsFromCapabilities(result, {
    layer: 'de_basemapde_web_raster_farbe', // todo: make configurable
    matrixSet: 'EPSG:3857',
    projection: 'EPSG:3857'
  });

  if (!optionsGray || !optionsColor) {
    throw new Error('Could not parse basemap.de layers from capabilities');
  }

  const baseMapLayerGray = new OlLayerTile({
    opacity: 1,
    source: new WMTS({
      attributions: basemapAttributions,
      ...optionsGray
    })
  });
  baseMapLayerGray.set('name', 'WebAtlas Grau');
  baseMapLayerGray.set('legendUrl', 'https://example.com'); // prevents getLegendGraphicRequest which isn't supported

  const baseMapLayerColor = new OlLayerTile({
    opacity: 1,
    source: new WMTS({
      attributions: basemapAttributions,
      ...optionsColor
    }),
    visible: false
  });
  baseMapLayerColor.set('name', 'WebAtlas Farbe');
  baseMapLayerColor.set('legendUrl', 'https://example.com'); // prevents getLegendGraphicRequest which isn't supported

  const dop20 = new ImageLayer({ // todo: make configurable
    extent: projectLayerExtent,
    visible: false,
    source: new ImageWMS({
      url: 'https://opendata.lgln.niedersachsen.de/doorman/noauth/dop_wms?',
      params: {
        'LAYERS': 'ni_dop20'
      },
      ratio: 1,
      serverType: 'geoserver',
      // eslint-disable-next-line max-len
      attributions: '© LGLN (2024) Creative Commons Namensnennung – 4.0 International (CC BY 4.0). Quelle: https://opengeodata.lgln.niedersachsen.de/#dop'
    })
  });
  dop20.set('name', 'Luftbilder');

  return [baseMapLayerGray, dop20];
};

const setupDefaultMap = async () => {
  const projectLayerGroup = new OlLayerGroup({
    layers: getProjectLayers()
  });
  projectLayerGroup.set('name', 'Themen');

  const additionalInfoLayerGroup = new OlLayerGroup({
    layers: getAdditionalInfoLayers()
  });
  additionalInfoLayerGroup.set('name', 'Weitere Informationen');

  backgroundLayers = await getBackGroundLayers();
  const backgroundLayerGroup = new OlLayerGroup({
    layers: backgroundLayers
  });
  backgroundLayerGroup.set('name', 'Hintergrund');

  const center = [937617.5, 6979400.5]; // todo: make configurable

  const fullScreenControl = new FullScreen({
    source: 'app',
    tipLabel: 'Vollbild-Modus umschalten'
  });

  const controls = OlControlDefaults({
    zoom: false
  }).extend([fullScreenControl]);

  return new OlMap({
    view: new OlView({
      center: center,
      constrainResolution: true,
      resolution: 140,
      resolutions: [ // todo: make configurable
        280, // 1:1.000.000
        140, // 1:500.000
        70, // 1:250.000
        28, // 1:100.000
        14, // 1:50.000
        7, // 1:25.000
        2.8, // 1:10.000
        1.4, // 1:5.000
        0.7, // 1:2.500
        0.28 // 1:1.000
      ]
    }),
    layers: [backgroundLayerGroup, additionalInfoLayerGroup, projectLayerGroup],
    controls
  });
};

const renderApp = async () => {
  const container = document.getElementById('app');
  const root = createRoot(container!);
  try {
    const map = await setupDefaultMap();

    root.render(
      <React.StrictMode>
        <React.Suspense fallback={<span></span>}>
          <ConfigProvider
            locale={getConfigLang(i18n.language)}
            theme={{
              token: {
                colorPrimary: '#1e6285'
              }
            }}
          >
            <Provider store={store}>
              <MapContext.Provider value={map}>
                <App backgroundLayers={backgroundLayers} />
              </MapContext.Provider>
            </Provider>
          </ConfigProvider>
        </React.Suspense>
      </React.StrictMode>
    );
  } catch (error) {
    Logger.error(error);

    const loadingMask = document.querySelectorAll('.loadmask')[0];

    if (loadingMask) {
      loadingMask.classList.add('loadmask-hidden');
    }

    root.render(
      <React.StrictMode>
        <Alert
          className="error-boundary"
          message={i18n.t('Index.errorMessage')}
          description={i18n.t('Index.errorDescription')}
          type="error"
          showIcon
        />
      </React.StrictMode>
    );
  }
};

renderApp();
